import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ReactElement } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { HasEntityMappings } from "../types/form-inputs.ts";
import { CardLayout } from "./CardLayout.tsx";
import { Button } from "./catalyst/Button.tsx";
import { Table } from "./catalyst/Table.tsx";
import { TableBody } from "./catalyst/TableBody.tsx";
import { TableCell } from "./catalyst/TableCell.tsx";
import { TableHead } from "./catalyst/TableHead.tsx";
import { TableHeader } from "./catalyst/TableHeader.tsx";
import { TableRow } from "./catalyst/TableRow.tsx";
import EntityMappingRow from "./EntityMappingRow.tsx";
import { Tooltip } from "./Tooltip.tsx";

interface EntityMappingTableProps {
  openDialog?: (index?: null | number) => void;
}

const MultiEntityMappingTable = ({
  openDialog,
}: EntityMappingTableProps): null | ReactElement => {
  const { control } = useFormContext<HasEntityMappings>();
  const { append, fields, remove } = useFieldArray({
    control,
    name: "entityMappings",
  });

  const addNewRow = (): void => {
    append({
      entityQualifiedName: "",
      isPersisted: false,
      isRoot: false,
      liquidTemplate: "",
      outputIsJson: false,
      relationship: "",
      value: "",
    });
  };

  const dataList = fields.map((entity, i) => (
    <EntityMappingRow
      entity={entity}
      index={i}
      key={entity.id}
      openDialog={openDialog}
      remove={remove}
    />
  ));

  const addButton = (
    <Button onClick={() => addNewRow()} outline>
      + Add
    </Button>
  );
  let tableBody = <>{dataList}</>;

  if (dataList.length == 0) {
    tableBody = (
      <TableRow>
        <TableCell
          className="border border-gray-300 py-4 text-center text-gray-500"
          colSpan={7}
        >
          <span className="mr-4 text-gray-500">No Entities</span>
          {addButton}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <CardLayout
      header={
        <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="ml-4 mt-2">Entity Mappings</div>
          <div className="ml-4 mt-2 flex space-x-2">{addButton}</div>
        </div>
      }
    >
      <Table className="w-full table-fixed" dense>
        <TableHead>
          <TableRow>
            <TableHeader className="w-10 text-left" />
            <TableHeader className="w-1/4">Output Entity Type</TableHeader>
            <TableHeader className="w-1/4">Liquid Template</TableHeader>
            <TableHeader className="w-1/4">Preview Value</TableHeader>
            <TableHeader className="w-1/6">Edge Relationship</TableHeader>
            <TableHeader className="w-20 whitespace-nowrap text-center">
              <span className="inline-flex items-center justify-center gap-1">
                <span>Is Root</span>
                <Tooltip
                  content="Identifies the entity as the root of the graph. Only one entity can be marked as root."
                  position="right"
                >
                  <InformationCircleIcon className="h-3 w-3 text-gray-500 hover:text-gray-700" />
                </Tooltip>
              </span>
            </TableHeader>
            <TableHeader className="w-10 text-right" />
          </TableRow>
        </TableHead>
        <TableBody>{tableBody}</TableBody>
      </Table>
    </CardLayout>
  );
};

export default MultiEntityMappingTable;
